import { FC, useMemo, useState } from "react";
import { IoCloseOutline, IoTimeOutline } from "react-icons/io5";
import { toast } from "react-toastify";

import Button from "../../../../../../components/button/Button";
import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import { WorkTaskModel } from "../../../../../../factories/work/model/work-task.model";
import { useUpdateWorkBudgetMutation } from "../../../../../../slices/WorkSlice";
import { getClampedPercent } from "./work-budget.utils";

type Props = {
  work: WorkDetailsModel;
  workTasks?: WorkTaskModel[];
  onClose: () => void;
};

const BUDGET_PROGRESS_COLOR = "#7B3FF2";

type BudgetRow = {
  id: string;
  name: string;
  statusName?: string;
  statusColor?: string;
  budgetedHours: string;
  trackedHours: number | null;
};

const formatHoursLabel = (hours: number): string => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (!minutes) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h ${minutes.toString().padStart(2, "0")}m`;
};

const formatTrackedHours = (hours: number | null): string => {
  if (hours === null) {
    return "";
  }

  return hours.toFixed(1);
};

const getTrackedHours = (duration?: number | null): number => {
  const durationSeconds = Number(duration);

  if (!Number.isFinite(durationSeconds)) {
    return 0;
  }

  return Math.max(durationSeconds / 3600, 0);
};

const normalizeBudgetValue = (value: string | number): number => {
  const budget = Number(value);

  return Number.isFinite(budget) ? Math.max(Math.floor(budget), 0) : 0;
};

const normalizeBudgetInputValue = (value: string): string => {
  return String(normalizeBudgetValue(value));
};

const getInitialTaskBudgets = (
  tasks: WorkTaskModel[] | undefined,
): Record<string, string> => {
  return (tasks || []).reduce<Record<string, string>>((acc, task) => {
    return {
      ...acc,
      [task.id]: String(normalizeBudgetValue(task.timeBudget)),
    };
  }, {});
};

const getBudgetRows = (
  tasks: WorkTaskModel[],
  taskBudgets: Record<string, string>,
): BudgetRow[] => {
  return tasks.map((task) => {
    return {
      id: task.id,
      name: task.name,
      statusName: task.subStatus?.subStatus || task.status?.name,
      statusColor: task.status?.color,
      budgetedHours:
        taskBudgets[task.id] ?? String(normalizeBudgetValue(task.timeBudget)),
      trackedHours: getTrackedHours(task.duration),
    };
  });
};

/** Edit Budget window: sized like the app's other small windows (Roadmap 1874). */
export const EditBudgetModal: FC<Props> = ({ work, workTasks, onClose }) => {
  const [updateWorkBudget, { isLoading: isSavingBudget }] =
    useUpdateWorkBudgetMutation();
  const [budgetHours, setBudgetHours] = useState(() =>
    normalizeBudgetValue(work.timeBudget),
  );
  const [taskBudgets, setTaskBudgets] = useState<Record<string, string>>(() =>
    getInitialTaskBudgets(workTasks),
  );

  const trackedHours = getTrackedHours(work.duration);
  const usedPercent = budgetHours
    ? Math.round((trackedHours / budgetHours) * 100)
    : 0;
  const progressWidth = getClampedPercent(usedPercent);

  const displayedTasks = useMemo(() => workTasks || [], [workTasks]);
  const budgetRows = useMemo(
    () => getBudgetRows(displayedTasks, taskBudgets),
    [displayedTasks, taskBudgets],
  );

  const handleTaskBudgetChange = (taskId: string, value: string) => {
    setTaskBudgets((currentBudgets) => ({
      ...currentBudgets,
      [taskId]: normalizeBudgetInputValue(value),
    }));
  };

  const handleSave = async () => {
    try {
      await updateWorkBudget({
        workId: work.id,
        budgetValue: normalizeBudgetValue(budgetHours),
        workTaskList: displayedTasks.map((task) => ({
          workTaskId: task.id,
          taskBudgetValue: normalizeBudgetValue(taskBudgets[task.id] ?? "0"),
        })),
      }).unwrap();

      toast.success("Budget updated successfully");
      onClose();
    } catch {
      toast.error("Failed to update budget");
    }
  };

  return (
    <div className={"modal modal-open"}>
      <div
        className={
          "modal-box flex max-h-[calc(100vh-48px)] w-[640px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[14px] p-0 font-Manrope text-[#1F2433]"
        }>
        <div
          className={
            "flex items-center justify-between border-b border-[#EDEFF3] px-5 py-3.5"
          }>
          <div className={"text-[16px] font-bold text-[#1F2433]"}>
            Edit Budget
          </div>
          <button
            type={"button"}
            onClick={onClose}
            className={
              "flex h-7 w-7 items-center justify-center rounded-lg text-[#9097A8] hover:bg-[#F1F2F5]"
            }
            aria-label={"Close edit budget modal"}>
            <IoCloseOutline size={20} />
          </button>
        </div>

        <div className={"overflow-y-auto px-5 py-4"}>
          <div className={"grid grid-cols-2 gap-3"}>
            <label>
              <div
                className={"mb-1.5 text-[12.5px] font-semibold text-[#3A4256]"}>
                Client
              </div>
              <input
                readOnly
                value={work.customer?.name || ""}
                className={
                  "input input-bordered h-9 w-full rounded-[9px] border-[#DCE2EA] bg-[#F4F6F8] px-3 text-[13px] font-medium"
                }
              />
            </label>

            <label>
              <div
                className={"mb-1.5 text-[12.5px] font-semibold text-[#3A4256]"}>
                Work Name
              </div>
              <input
                readOnly
                value={work.name || ""}
                className={
                  "input input-bordered h-9 w-full rounded-[9px] border-[#DCE2EA] bg-[#F4F6F8] px-3 text-[13px] font-medium"
                }
              />
            </label>
          </div>

          <div
            className={
              "mt-4 grid grid-cols-[220px_1fr] items-center gap-6 rounded-[10px] border border-[#EDEFF3] px-4 py-4 max-sm:grid-cols-1"
            }>
            <div className={"grid grid-cols-[20px_1fr] items-center gap-2.5"}>
              <IoTimeOutline size={18} className={"text-[#7B66F0]"} />
              <div>
                <div className={"mb-1.5 text-[13px] font-bold text-[#1F2433]"}>
                  Work Budget
                </div>
                <div className={"flex items-center gap-2"}>
                  <input
                    type={"number"}
                    min={0}
                    step={1}
                    value={budgetHours}
                    onChange={(event) =>
                      setBudgetHours(normalizeBudgetValue(event.target.value))
                    }
                    className={
                      "input input-bordered h-9 w-[110px] rounded-[9px] border-[#DCE2EA] px-3 text-right text-[13px] font-medium"
                    }
                  />
                  <span className={"text-[13px] text-[#6B7185]"}>hours</span>
                </div>
              </div>
            </div>

            <div>
              <div className={"mb-1.5 text-[13px] font-bold text-[#1F2433]"}>
                Total Hours Worked
              </div>
              <div className={"text-[12.5px] font-medium text-[#6B7185]"}>
                <span className={"text-[16px] font-bold text-[#7B3FF2]"}>
                  {formatHoursLabel(trackedHours)}
                </span>{" "}
                of {budgetHours} hours ({usedPercent}%)
              </div>
              <div className={"mt-2.5 h-[6px] w-full rounded-md bg-[#F4F6F8]"}>
                <div
                  className={"h-full rounded-md"}
                  style={{
                    width: `${progressWidth}%`,
                    backgroundColor: BUDGET_PROGRESS_COLOR,
                  }}
                />
              </div>
            </div>
          </div>

          <div className={"mt-4"}>
            <div className={"mb-2 flex items-center justify-between"}>
              <div className={"text-[14px] font-bold text-[#1F2433]"}>
                Subtasks
              </div>
              <div className={"text-[12px] font-medium text-[#6B7185]"}>
                Budget in hours
              </div>
            </div>

            <div
              className={
                "overflow-hidden rounded-[10px] border border-[#EDEFF3] bg-white"
              }>
              <div
                className={
                  "grid grid-cols-[minmax(0,1fr)_90px_100px] bg-[#F8FAFC] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
                }>
                <div>Subtasks</div>
                <div className={"text-center"}>Budget</div>
                <div className={"text-center"}>Hours worked</div>
              </div>

              {budgetRows.length > 0 ? (
                <div className={"max-h-[280px] overflow-y-auto"}>
                  {budgetRows.map((task) => (
                    <div
                      key={task.id}
                      className={
                        "grid grid-cols-[minmax(0,1fr)_90px_100px] items-center border-t border-[#F0F1F4] px-3 py-2 text-[13px]"
                      }>
                      <div className={"min-w-0"}>
                        <div
                          className={"truncate font-semibold text-[#1F2433]"}
                          title={task.name}>
                          {task.name}
                        </div>
                        {task.statusName && (
                          <div
                            className={
                              "mt-0.5 flex w-fit items-center gap-1 rounded-md bg-[#F4F6F8] px-1.5 py-px text-[11px] font-medium text-[#6B7185]"
                            }>
                            {task.statusColor && (
                              <span
                                className={"h-2 w-2 rounded-full"}
                                style={{ backgroundColor: task.statusColor }}
                              />
                            )}
                            {task.statusName}
                          </div>
                        )}
                      </div>

                      <div className={"flex justify-center"}>
                        <input
                          type={"number"}
                          min={0}
                          step={1}
                          value={task.budgetedHours}
                          onChange={(event) =>
                            handleTaskBudgetChange(task.id, event.target.value)
                          }
                          className={
                            "input input-bordered h-8 w-[64px] rounded-[8px] border-[#DCE2EA] px-2 text-right text-[13px] font-medium"
                          }
                        />
                      </div>

                      <div className={"flex justify-center"}>
                        <input
                          readOnly
                          value={formatTrackedHours(task.trackedHours)}
                          className={
                            "input input-bordered h-8 w-[72px] rounded-[8px] border-[#DCE2EA] bg-[#F4F6F8] px-2 text-right text-[13px] font-medium text-[#6B7185]"
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className={
                    "border-t border-[#F0F1F4] px-3 py-6 text-center text-[13px] text-[#6B7185]"
                  }>
                  No subtasks are available for this work item.
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={
            "flex justify-end gap-2.5 border-t border-[#EDEFF3] px-5 py-3.5"
          }>
          <Button
            label={"Cancel"}
            colorType={"outline"}
            size={"custom"}
            buttonType={"button"}
            onClick={onClose}
            extraClasses={
              "h-9 min-h-fit w-[100px] rounded-[9px] border-[#DCE2EA] text-[13px] text-[#3A4256] normal-case"
            }
          />
          <Button
            label={"Save"}
            colorType={"secondary"}
            size={"custom"}
            buttonType={"button"}
            onClick={handleSave}
            disabled={isSavingBudget}
            isLoading={isSavingBudget}
            extraClasses={
              "h-9 min-h-fit w-[100px] rounded-[9px] text-[13px] text-white normal-case"
            }
          />
        </div>
      </div>
    </div>
  );
};
